// SPEC-0022 load tiers: thresholds + design tokens (never colours). The tier
// colour resolves at render from the token (resourceChip.scss), so a tier
// change recolours the chips with no recompute.
import i18n from "../../../../i18n";

export const DEFAULT_LOAD_TIERS = [{ threshold: 100, token: "warning" }, { threshold: 110, token: "danger" }];
export const LOAD_TIER_TOKENS = ["info", "caution", "warning", "danger"];


const isValidTier = (tier) =>
  typeof tier?.threshold === "number" && Number.isFinite(tier.threshold) && tier.threshold > 0
  && LOAD_TIER_TOKENS.includes(tier.token);

// What the options store keeps: valid entries only, ascending, at most four;
// the defaults when nothing valid is left or the result still fails validation.
export const normalizeLoadTiers = (tiers) => {
  if (!Array.isArray(tiers)) return DEFAULT_LOAD_TIERS;
  const kept = tiers.filter(isValidTier)
    .map(({ threshold, token }) => ({ threshold, token }))
    .sort((a, b) => a.threshold - b.threshold)
    .slice(0, 4);
  return kept.length && validateLoadTiers(kept) == null ? kept : DEFAULT_LOAD_TIERS;
};

const formats = new Map();
const numberFormat = (locale, digits) => {
  const k = `${locale}|${digits}`;
  if (!formats.has(k)) {
    formats.set(k, new Intl.NumberFormat(locale, { maximumFractionDigits: digits, minimumFractionDigits: digits }));
  }
  return formats.get(k);
};

// Integer from 10 up, one decimal below. The % sign placement is the
// language's (en "123%", fr "123 %" with a non-breaking space) via i18n.
export const formatLoadPct = (pct, language = "en") => {
  const digits = Math.abs(pct) < 10 ? 1 : 0;
  const n = numberFormat(language === "fr" ? "fr-FR" : "en-US", digits).format(pct);
  return i18n.t("resourceChip.badge.pct", { lng: language, pct: n });
};

// Backend LoadTierRules.MAX_THRESHOLD: the stored peak is capped at 9999.9 %.
export const MAX_LOAD_TIER_THRESHOLD = 9999;

// Mirrors the backend validation; returns null or the backend error code.
export const validateLoadTiers = (tiers) => {
  if (!Array.isArray(tiers) || tiers.length < 1 || tiers.length > 4) return "LOAD_TIERS_COUNT";
  let prev = null;
  for (const tier of tiers) {
    const th = typeof tier?.threshold === "number" ? tier.threshold : Number.NaN;
    if (!(th > 0)) return "LOAD_TIERS_THRESHOLD_POSITIVE";
    if (th > MAX_LOAD_TIER_THRESHOLD) return "LOAD_TIERS_THRESHOLD_MAX";
    if (prev != null && th <= prev) return "LOAD_TIERS_THRESHOLD_INCREASING";
    if (!LOAD_TIER_TOKENS.includes(tier.token)) return "LOAD_TIERS_TOKEN";
    prev = th;
  }
  return null;
};
